import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// RTL languages list
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (language: string) => void;
  t: (key: string, options?: any) => string;
  isRTL: boolean;
  direction: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n, t } = useTranslation();

  const currentLanguage = i18n.language || 'fr';
  const isRTL = RTL_LANGUAGES.includes(currentLanguage);
  const direction = isRTL ? 'rtl' : 'ltr';

  const setLanguage = (language: string) => {
    // Supported locales: fr, en, ar
    if (['fr', 'en', 'ar'].includes(language)) {
      i18n.changeLanguage(language);
      // i18next-browser-languagedetector handles localStorage automatically
    }
  };

  useEffect(() => {
    // If language is not set (should be handled by detector, but as fallback)
    if (!i18n.language) {
      i18n.changeLanguage('fr');
    }
  }, [i18n]);

  // Update HTML dir attribute when language changes
  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', currentLanguage);
  }, [direction, currentLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t, isRTL, direction }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
